/**
 * whiteboardProjectionEdit — direct manipulation of the projected layer (rung 3).
 *
 * The projection view can run on a touch panel / interactive display. Rather than
 * re-photographing the board, you edit the projected v1 IN PLACE — drag a box to
 * a new spot, wipe one away to delete it — and save back to a new version with
 * identities preserved. This module is the pure core: given the baseline and a
 * list of edits, it computes the next baseline plus the side-effects an apply
 * needs (which items moved to a new altitude, which were removed, which
 * relationships fell away with them). No DOM, no stores.
 */

import { altitudeFromPosition } from './whiteboardImportService';
import type { BoardBaseline, BaselineItem, BaselineRelationship } from './whiteboardMerge';
import type { AltitudeLevel } from '../types/prioritization/itemLifecycle';

export interface MoveEdit {
  type: 'move';
  id: string;
  position: { x: number; y: number };
}
export interface DeleteEdit {
  type: 'delete';
  id: string;
}
export type BoardEdit = MoveEdit | DeleteEdit;

export interface EditPlan {
  /** The board after the edits — positions updated, deleted items/edges gone. */
  nextBaseline: BoardBaseline;
  /** Items that changed position, with the altitude their new height implies. */
  moved: Array<{ id: string; position: { x: number; y: number }; altitudeLevel: AltitudeLevel }>;
  /** Ids of items wiped away on the board (callers archive these). */
  removedItemIds: string[];
  /** Relationships dropped because an endpoint was removed. */
  removedRelationships: BaselineRelationship[];
  summary: { moved: number; removed: number; relationshipsRemoved: number };
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0.5;
  return Math.min(1, Math.max(0, n));
}

function samePos(a: { x: number; y: number }, b: { x: number; y: number }): boolean {
  return Math.abs(a.x - b.x) < 1e-9 && Math.abs(a.y - b.y) < 1e-9;
}

/**
 * Fold a list of edits onto a baseline and produce the next version plus the
 * apply side-effects. Pure & deterministic. Edits are applied in order: a later
 * move for an id supersedes an earlier one, and a delete wins over any move for
 * the same id regardless of order. Edits referencing unknown ids are ignored.
 */
export function planBoardEdits(baseline: BoardBaseline, edits: BoardEdit[]): EditPlan {
  const known = new Set(baseline.items.map((i) => i.id));
  const deleted = new Set<string>();
  const moves = new Map<string, { x: number; y: number }>();

  for (const edit of edits) {
    if (!known.has(edit.id)) continue;
    if (edit.type === 'delete') {
      deleted.add(edit.id);
      moves.delete(edit.id);
    } else {
      if (deleted.has(edit.id)) continue;
      moves.set(edit.id, { x: clamp01(edit.position.x), y: clamp01(edit.position.y) });
    }
  }

  const items: BaselineItem[] = [];
  const moved: EditPlan['moved'] = [];
  for (const it of baseline.items) {
    if (deleted.has(it.id)) continue;
    const movedTo = moves.get(it.id);
    if (movedTo && !samePos(movedTo, it.position)) {
      items.push({ ...it, position: movedTo });
      moved.push({ id: it.id, position: movedTo, altitudeLevel: altitudeFromPosition(movedTo.y) });
    } else {
      items.push({ ...it });
    }
  }

  const removedRelationships = baseline.relationships.filter(
    (r) => deleted.has(r.sourceItemId) || deleted.has(r.targetItemId),
  );
  const relationships = baseline.relationships.filter(
    (r) => !deleted.has(r.sourceItemId) && !deleted.has(r.targetItemId),
  );

  const removedItemIds = [...deleted];

  return {
    nextBaseline: {
      versionId:
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `wb-${Date.now().toString(36)}`,
      capturedAt: new Date().toISOString(),
      items,
      relationships,
    },
    moved,
    removedItemIds,
    removedRelationships,
    summary: {
      moved: moved.length,
      removed: removedItemIds.length,
      relationshipsRemoved: removedRelationships.length,
    },
  };
}
